import { useEffect, useState } from "react";
import "./index.scss";
import "./register-stepper.scss"; // Import new stepper styles
import { useDispatch } from "react-redux";
import { useNavigate, Link, useLocation } from "react-router-dom";
import store from "store";
import { Actions as ApiCallActions } from "../../../redux/apiCall/reducers";
import { Actions as AuthActions } from "../../../redux/auth/reducers";
import KvkkModal from "../../../components/kvkk-modal";
import general from "../../../utils/general";
import LanguageSwitcher from "../../../components/language-switcher";
import useI18n from "../../../i18n/useI18n";
import RegisterFormContent from "./components/RegisterFormContent";
import VerificationContent from "./components/VerificationContent";
import RegisterProfileComplete from "./components/RegisterProfileComplete";

const assetStylesheets = [
    "/assets/plugins/bootstrap/css/bootstrap.min.css",
    "/assets/css/main.css",
    "/assets/css/authentication.css",
    "/assets/css/color_skins.css",
];

const RegisterWithAgent = () => {
    const dispatch = useDispatch();
    const navigate = useNavigate();
    const location = useLocation();
    const { t } = useI18n();
    const [navOpen, setNavOpen] = useState(false);

    // Stepper State
    const [currentStep, setCurrentStep] = useState(1); // 1: Register/Verify, 2: Profile

    const [formData, setFormData] = useState({
        email: "",
        password: "",
        firstName: "",
        lastName: "",
        phone: "",
        userType: "student", // Default olarak öğrenci seçili
        agentId: "",
        kvkkAccepted: false,
    });
    const [validationErrors, setValidationErrors] = useState({});
    const [isFormValid, setIsFormValid] = useState(false);
    const [showKvkkError, setShowKvkkError] = useState(false);
    const [isKvkkModalOpen, setIsKvkkModalOpen] = useState(false);
    const [isUserTypeLocked, setIsUserTypeLocked] = useState(false);

    // Verification State
    const [isVerificationStep, setIsVerificationStep] = useState(false);
    const [verificationCode, setVerificationCode] = useState("");
    const [verificationCodeError, setVerificationCodeError] = useState("");
    const [pendingEmail, setPendingEmail] = useState("");
    const [isVerifying, setIsVerifying] = useState(false);

    const computeFormValidity = (errors, kvkkAccepted) =>
        Object.values(errors).every((error) => error === "") && kvkkAccepted;

    // CSS'ler route helper tarafından yönetiliyor, burada sadece yüklendiğinden emin oluyoruz
    useEffect(() => {
        assetStylesheets.forEach((href) => {
            const existing = document.querySelector(`link[href="${href}"]`);
            if (!existing) {
                const link = document.createElement("link");
                link.rel = "stylesheet";
                link.href = href;
                document.head.appendChild(link);
            }
        });
    }, []);

    useEffect(() => {
        const html = document.documentElement;
        if (navOpen) {
            html.classList.add("nav-open");
        } else {
            html.classList.remove("nav-open");
        }
        return () => html.classList.remove("nav-open");
    }, [navOpen]);

    // URL'den gelen agent ve usertype parametrelerini oku; varsa formu kilitle
    useEffect(() => {
        const searchParams = new URLSearchParams(location.search);
        const urlUserType = searchParams.get("usertype");
        const urlAgentId = searchParams.get("agent");

        if (urlUserType === "student" && urlAgentId) {
            setFormData((prev) => ({
                ...prev,
                userType: "student",
                agentId: urlAgentId,
            }));
            setIsUserTypeLocked(true);
        }
    }, [location.search]);

    // Check if user is already logged in and needs to complete profile
    useEffect(() => {
        const checkExistingSession = () => {
            const token = store.get("token");
            const userInfo = store.get("userInfo");
            // Could check session validity here
        };
        checkExistingSession();
    }, []);

    const handleChange = (event) => {
        const { name, value, type, checked } = event.target;
        const newValue = type === "checkbox" ? checked : value;
        setFormData((prev) => ({
            ...prev,
            [name]: newValue,
        }));

        if (name === "kvkkAccepted") {
            setShowKvkkError(!newValue);
            setIsFormValid(computeFormValidity(validationErrors, newValue));
        }
    };

    const handleUserTypeChange = (userType) => {
        if (isUserTypeLocked) return;
        setFormData((prev) => ({
            ...prev,
            userType: userType,
        }));
    };

    const handleValidationChange = (fieldName, isValid, errorMessage) => {
        const updatedErrors = {
            ...validationErrors,
            [fieldName]: isValid ? "" : errorMessage,
        };

        setValidationErrors(updatedErrors);
        setIsFormValid(computeFormValidity(updatedErrors, formData.kvkkAccepted));
    };

    const handleVerificationValidationChange = (fieldName, isValid, errorMessage) => {
        if (fieldName !== "verificationCode") return;
        setVerificationCodeError(isValid ? "" : errorMessage);
    };

    const handleSubmit = (event) => {
        event.preventDefault();

        if (!formData.kvkkAccepted) {
            setShowKvkkError(true);
            general.notificationError(t("AUTH_REGISTER_KVKK_ERROR"));
            return;
        }

        if (!isFormValid) {
            general.notificationError(t("AUTH_REGISTER_FIX_ERRORS"));
            return;
        }

        dispatch(
            ApiCallActions.Post({
                url: "auth/register",
                data: formData,
                showAlertOnError: true,
                showLoading: true,
                onSuccess: async ({ data }) => {
                    general.messageSuccess(t("AUTH_REGISTER_SUCCESS"));
                    general.messageSuccess("E-posta doğrulama kodu gönderildi. Lütfen e-postanızı kontrol edin.");
                    setPendingEmail(formData.email);
                    setIsVerificationStep(true);
                },
                onError: ({ errorMessage }) => {
                    general.notificationError(errorMessage)
                },
            })
        );
    };

    const handleVerifyEmail = (event) => {
        event.preventDefault();
        const emailToVerify = pendingEmail || formData.email;
        const codeToVerify = verificationCode.trim();

        if (!emailToVerify) {
            general.notificationError("E-posta bulunamadı. Lütfen tekrar kayıt olun.");
            return;
        }

        if (!codeToVerify) {
            general.notificationError("Lütfen e-posta ile gelen kodu giriniz.");
            return;
        }

        if (verificationCodeError) {
            general.notificationError(verificationCodeError);
            return;
        }

        setIsVerifying(true);
        dispatch(
            ApiCallActions.Post({
                url: "auth/verify-email",
                data: { email: emailToVerify, code: codeToVerify },
                showAlertOnError: true,
                showLoading: true,
                onSuccess: () => {
                    general.messageSuccess("E-posta doğrulandı.");

                    // Eğer agent ile geldiyse otomatik login yap
                    if (formData.agentId) {
                        handleAutoLogin();
                    } else {
                        general.messageSuccess("Otomatik giriş yapılıyor...");
                        handleAutoLogin();
                    }
                },
                onError: ({ errorMessage }) => {
                    if (errorMessage) {
                        general.notificationError(errorMessage);
                    }
                    setIsVerifying(false);
                },
            })
        );
    };


    const handleAutoLogin = () => {
        const loginData = {
            email: formData.email,
            password: formData.password
        };

        dispatch(
            ApiCallActions.Post({
                url: "auth/login",
                data: loginData,
                showAlertOnError: true,
                showLoading: true,
                onSuccess: async ({ data }) => {
                    const tokens = data?.data?.tokens || data?.tokens;

                    if (tokens && tokens.accessToken) {
                        store.set("token", tokens.accessToken);
                        if (tokens.refreshToken) {
                            store.set("refreshToken", tokens.refreshToken);
                        }
                        if (tokens.expiresAt) {
                            store.set("tokenExpiresAt", tokens.expiresAt);
                        }

                        dispatch(
                            ApiCallActions.Get({
                                url: 'auth/profile',
                                showAlertOnError: false,
                                showLoading: false,
                                onSuccess: ({ data }) => {
                                    const profileData = data?.data || data;
                                    store.set("userInfo", profileData);
                                    dispatch(AuthActions.setAuthState({
                                        userInfo: profileData,
                                        isUserAuthorized: true
                                    }));

                                    // Login başarılı, profil tamamlama adımına geç (Step 2)
                                    setIsVerifying(false);
                                    setIsVerificationStep(false);
                                    setCurrentStep(2); // Move to Step 2
                                },
                                onError: () => {
                                    console.error('Profile fetch error');
                                    navigate("/auth/login");
                                }
                            })
                        );
                    } else {
                        navigate("/auth/login");
                    }
                },
                onError: () => {
                    setIsVerifying(false);
                    navigate("/auth/login");
                }
            })
        );
    }

    // Determine header title based on state
    const getHeaderTitle = () => {
        if (currentStep === 2) return "Profil Bilgileri";
        if (isVerificationStep) return "E-posta Doğrulama";
        return t("AUTH_REGISTER_TITLE");
    }

    return (
        <section className="theme-blush authentication sidebar-collapse">
            <nav className="navbar navbar-expand-lg fixed-top navbar-transparent mobile-agent-nav">
                <div className="container-fluid" style={{ justifyContent: 'space-between', padding: '0 30px' }}>
                    <div className="navbar-translate" id="agent-logo-container">
                        <a className="navbar-brand" href="/" style={{ padding: 0 }}>
                            <img
                                src="https://www.yosdanismanim.com/wp-content/uploads/2025/11/yos-logo.png"
                                alt="YÖS Portal"
                                style={{ height: '50px' }}
                            />
                        </a>
                    </div>

                    <div>
                        <ul className="navbar-nav navbar-flex-row">
                            <li className="nav-item">
                                <a className="nav-link" href="https://www.yosdanismanim.com/" target="_blank" rel="noreferrer">
                                    {t("COMMON_HOME")}
                                </a>
                            </li>
                            <li className="nav-item">
                                <a
                                    className="nav-link"
                                    title={t("AUTH_NAV_TWITTER_TITLE")}
                                    href="https://x.com/yosdanismanim"
                                    target="_blank"
                                    rel="noreferrer"
                                >
                                    <i className="zmdi zmdi-twitter"></i>
                                </a>
                            </li>
                            <li className="nav-item">
                                <a
                                    className="nav-link"
                                    title={t("AUTH_NAV_YOUTUBE_TITLE")}
                                    href="https://www.youtube.com/@yosdanismanim"
                                    target="_blank"
                                    rel="noreferrer"
                                >
                                    <i className="zmdi zmdi-youtube"></i>
                                </a>
                            </li>
                            <li className="nav-item">
                                <a
                                    className="nav-link"
                                    title={t("AUTH_NAV_INSTAGRAM_TITLE")}
                                    href="https://www.instagram.com/yosdanismanim/?igsh=MTZ4cTB3ejBwcDVqbg%3D%3D#"
                                    target="_blank"
                                    rel="noreferrer"
                                >
                                    <i className="zmdi zmdi-instagram"></i>
                                </a>
                            </li>
                            <li className="nav-item" >
                                <div className="navbar-lang-switcher" style={{ marginTop: 13, marginLeft: 5 }}>
                                    <LanguageSwitcher />
                                </div>
                            </li>
                        </ul>
                    </div>
                </div>
            </nav>

            <div className="page-header agent-register-header">
                <div className="page-header-image register-page-header-image"></div>
                <div className="container">
                    <div className="col-md-12">
                        <div className={`register-card-wide container-step-${currentStep}`}>

                            {/* Stepper Indicators */}
                            <div className={`register-stepper step-${currentStep}`}>
                                <div className={`step ${currentStep >= 1 ? 'active' : ''} ${currentStep > 1 ? 'completed' : ''}`}>
                                    <div className="step-circle">
                                        {currentStep > 1 ? <i className="zmdi zmdi-check"></i> : 1}
                                    </div>
                                    <div className="step-label">{t("AUTH_REGISTER_STEP_1")}</div>
                                </div>
                                <div className={`step ${currentStep >= 2 ? 'active' : ''} ${currentStep > 2 ? 'completed' : ''}`}>
                                    <div className="step-circle">2</div>
                                    <div className="step-label">{t("AUTH_REGISTER_STEP_2")}</div>
                                </div>
                            </div>

                            <div className="content">
                                {currentStep === 2 ? (
                                    // Step 2: Profile Complete
                                    <div className="register-profile-complete-wrapper">
                                        <RegisterProfileComplete />
                                    </div>
                                ) : isVerificationStep ? (
                                    // Step 1: Verification
                                    <div className="register-step-1-wrapper">
                                        <form onSubmit={handleVerifyEmail}>
                                            <VerificationContent
                                                pendingEmail={pendingEmail}
                                                fallbackEmail={formData.email}
                                                verificationCode={verificationCode}
                                                setVerificationCode={setVerificationCode}
                                                handleVerificationValidationChange={handleVerificationValidationChange}
                                            />
                                            <div className="footer text-center mt-3">
                                                <button
                                                    type="submit"
                                                    className="btn btn-warning btn-round btn-lg btn-block"
                                                    disabled={isVerifying}
                                                >
                                                    {isVerifying ? "Doğrulanıyor..." : "Kodu Doğrula"}
                                                </button>
                                                <h5>
                                                    <Link to="/auth/login" className="link">
                                                        {t("AUTH_REGISTER_LOGIN_CTA")}
                                                    </Link>
                                                </h5>
                                            </div>
                                        </form>
                                    </div>
                                ) : (
                                    // Step 1: Register Form
                                    <div className="register-step-1-wrapper">
                                        <form onSubmit={handleSubmit}>
                                            <RegisterFormContent
                                                formData={formData}
                                                handleChange={handleChange}
                                                handleUserTypeChange={handleUserTypeChange}
                                                isUserTypeLocked={isUserTypeLocked}
                                                handleValidationChange={handleValidationChange}
                                                t={t}
                                                showKvkkError={showKvkkError}
                                                onKvkkLinkClick={() => setIsKvkkModalOpen(true)}
                                            />
                                            <div className="footer text-center mt-3">
                                                <button
                                                    type="submit"
                                                    className="btn btn-warning btn-round btn-lg btn-block"
                                                >
                                                    {t("AUTH_REGISTER_SUBMIT")}
                                                </button>
                                                <h5>
                                                    <Link to="/auth/login" className="link">
                                                        {t("AUTH_REGISTER_HAVE_ACCOUNT")}{" "}
                                                        <span className="text-warning">{t("AUTH_REGISTER_LOGIN_CTA")}</span>
                                                    </Link>
                                                </h5>
                                            </div>
                                        </form>
                                    </div>
                                )}
                            </div>
                        </div>
                    </div>
                </div>
                <KvkkModal isOpen={isKvkkModalOpen} onClose={() => setIsKvkkModalOpen(false)} />
                <footer className="footer">
                    <div className="container">
                        <nav>
                            <ul>
                                <li>
                                    <a href="http://thememakker.com/contact/" target="_blank" rel="noreferrer">
                                        {t("AUTH_FOOTER_CONTACT")}
                                    </a>
                                </li>
                                <li>
                                    <a href="http://thememakker.com/about/" target="_blank" rel="noreferrer">
                                        {t("AUTH_FOOTER_ABOUT")}
                                    </a>
                                </li>
                                <li>
                                    <a href="javascript:void(0);">{t("AUTH_FOOTER_FAQ")}</a>
                                </li>
                            </ul>
                        </nav>
                        <div className="copyright">
                            &copy; {new Date().getFullYear()},{" "}
                            <span>
                                <a className="text-warning" href="http://algoraft.com/" target="_blank" rel="noreferrer">
                                    Algoraft.com
                                </a>
                                &nbsp;{t("AUTH_FOOTER_DESIGNED_BY")}{" "}
                            </span>
                        </div>
                    </div>
                </footer>

            </div>
        </section>
    );
};

export default RegisterWithAgent;
